<template>
  <Teleport to="body">
    <div
      class="fixed z-[60] w-80 overflow-hidden rounded-lg border border-land-border bg-white text-left shadow-2xl"
      :style="{ top: `${position.top}px`, left: `${position.left}px` }"
      role="dialog"
      :aria-label="`About ${layer.name}`"
    >
      <div class="border-b border-land-border-soft p-3.5">
        <div class="flex items-start gap-2">
          <span class="mt-1 size-3 shrink-0 rounded-sm border border-white shadow-sm" :style="{ backgroundColor: accentColor }" aria-hidden="true" />
          <p class="text-sm font-bold leading-snug text-land-ink">{{ layer.name }}</p>
        </div>
        <div class="mt-2 flex flex-wrap gap-1.5">
          <span v-if="layer.categoryLabel" class="rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">{{ layer.categoryLabel }}</span>
          <span v-if="geometryLabel" class="rounded-full border border-land-border-soft bg-land-surface-muted px-2 py-0.5 text-[11px] font-semibold text-land-ink-soft">{{ geometryLabel }}</span>
          <span v-if="layer.provider" class="rounded-full border border-land-border-soft bg-land-surface-muted px-2 py-0.5 text-[11px] font-semibold text-land-ink-soft">{{ layer.provider }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-3 p-3.5">
        <div v-if="layer.description">
          <p class="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-land-ink-soft">What it is</p>
          <p class="text-[13px] leading-relaxed text-land-ink-muted">{{ layer.description }}</p>
        </div>

        <div v-if="layer.usage">
          <p class="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-land-ink-soft">How to use it</p>
          <p class="text-[13px] leading-relaxed text-land-ink-muted">{{ layer.usage }}</p>
        </div>

        <div v-if="layer.caveats && layer.caveats.length > 0">
          <p class="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-land-ink-soft">Caveats</p>
          <ul class="flex flex-col gap-1.5">
            <li v-for="(caveat, index) in layer.caveats" :key="index" class="relative pl-5 text-[13px] leading-snug text-land-ink-muted">
              <span class="absolute left-0 top-0.5 grid size-3.5 place-items-center rounded-full bg-amber-100 text-[9px] font-extrabold text-amber-700" aria-hidden="true">!</span>
              {{ caveat }}
            </li>
          </ul>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-land-border-soft bg-land-surface-muted px-3.5 py-2.5 text-[11.5px] text-land-ink-soft">
        <span><span class="text-land-ink-muted">Source:</span> {{ layer.provider || 'Unknown' }}</span>
        <span v-if="refreshCadence"><span class="text-land-ink-muted">Updated:</span> {{ refreshCadence }}</span>
        <a v-if="layer.sourceUrl" :href="layer.sourceUrl" target="_blank" rel="noreferrer" class="font-semibold text-land-primary hover:text-land-primary-hover">Open source ↗</a>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  },
  position: {
    type: Object,
    default: () => ({ top: 0, left: 0, placement: 'right' })
  }
})

const accentColor = computed(() => props.layer.style?.fillColor || props.layer.style?.lineColor || props.layer.style?.circleColor || '#64748b')
const geometryLabel = computed(() => ({ polygon: 'Polygon', line: 'Line', point: 'Point' })[props.layer.geometryType] || '')
const refreshCadence = computed(() => props.layer.metadata?.refreshCadence || '')
</script>
